import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Tooltip, IconButton } from '@mui/material';
import { DataGrid, GridSortModel } from '@mui/x-data-grid';

import { InfraPlan } from '../../../models/plan/plan';
// PlannenActionMenu import removed - action menu temporarily hidden
import BeoordelingenDetailPanel from './BeoordelingenDetailPanel';
import DialogBeoordelingAdd from '../dialogs/DialogBeoordelingAdd';
import { authService } from '../../../services/auth.service';
import { contentService } from '../../../services/content.service';
import { MijnOrganisatieListItem } from '../../../models/user/mijnOrganisatieListItem';
import { helperService } from '../../../services/helper.service';
import { eventBus } from '../../../services/event-emitter.service';
import { planManagementService } from '../../../services/plan-management.service';


interface TablePlannenProps {
  plans: InfraPlan[]; // ID of the current organisatie
  pageIndex: number; // Page index
  pageSize: number; 
  totalCount: number; // Total count of plans
  panelWidth: number;  // Receive panel width as prop
  mijnOrganisatie: MijnOrganisatieListItem; // ID of the current organisatie
  onPagination: (pageIndex: number, pageSize: number) => void; // callback function to trigger refresh
  onSorting: (sortBy: string, sortDirection: string) => void; // New prop
  sortBy?: string;
  sortDirection?: string;
  onClickPlan: (planId: string) => void; // callback function to trigger refresh
}

const LS_VISIBLE_COLUMN_SETTING_KEY = "infplVisibleColumnSettings";

const SORT_FIELD_MAP: Record<string, string> = {
  PlanId: 'PlanId',
  PlanNaam: 'PlanNaam',
  Discipline: 'Discipline',
  OrganizationName: 'InfplOrganizationName',
  PlanType: 'PlanType',
  PlanStatus: 'PlanStatus',
  PlanStartDatum: 'PlanStartDatum',
  PlanEindDatum: 'PlanEindDatum',
  Kosten: 'Kosten',
};

const getBreakpoint = (width: number) => {
  if (width < 201) return "xs";
  if (width < 401) return "sm";
  if (width < 601) return "md";
  if (width < 801) return "lg";
  if (width < 1001) return "xl";
  return "xxl";
};

const TablePlannen: React.FC<TablePlannenProps> = ({
  plans,
  pageIndex,
  pageSize,
  totalCount,
  panelWidth,
  mijnOrganisatie,
  onPagination,
  onSorting,
  sortBy,
  sortDirection,
  onClickPlan
}) => {

  const [paginationModel, setPaginationModel] = React.useState({
    page: 0,
    pageSize: 25,
  });

  const breakpoint = getBreakpoint(panelWidth);

  // State voor geselecteerd plan op de kaart
  const [selectedPlanId, setSelectedPlanId] = useState<string | null>(null);

  // State voor detail panel
  const [expandedDetailPanels, setExpandedDetailPanels] = useState<Record<string, boolean>>({});

  // State voor beoordeling dialog
  const [beoordelingDialogOpen, setBeoordelingDialogOpen] = useState(false);
  const [selectedPlanForBeoordeling, setSelectedPlanForBeoordeling] = useState<InfraPlan | null>(null);
  const [beoordelingRefreshKeys, setBeoordelingRefreshKeys] = useState<Record<string, number>>({});

  const handleOpenBeoordelingDialog = (plan: InfraPlan) => {
    setSelectedPlanForBeoordeling(plan);
    setBeoordelingDialogOpen(true);
  };

  const handleCloseBeoordelingDialog = () => {
    setBeoordelingDialogOpen(false);
    setSelectedPlanForBeoordeling(null);
  };

  const handleSubmitBeoordeling = async (beoordeling: any) => {
    if (!selectedPlanForBeoordeling?.infplId) return;
    try {
      const response = await planManagementService.addPlanBeoordeling(beoordeling, selectedPlanForBeoordeling.infplId);
      if (response.ok) {
        handleCloseBeoordelingDialog();
        refetchPlannen();
        setBeoordelingRefreshKeys(prev => ({
          ...prev,
          [selectedPlanForBeoordeling.infplId!]: (prev[selectedPlanForBeoordeling.infplId!] ?? 0) + 1,
        }));
      }
    }
    catch (error) {
      console.error('Error saving beoordeling:', error);
    }
  };

  // Listen to selectPlan events from the map or other components
  useEffect(() => {
    const handleSelectPlan = (planId: string | null) => {
      setSelectedPlanId(planId);
    };
    
    eventBus.on('selectPlan', handleSelectPlan);
    
    return () => {
      eventBus.off('selectPlan', handleSelectPlan);
    };
  }, []);

  // Alleen plan op de kaart highlighten
  const handleZoomInOnPlanBbox = (plan: InfraPlan) => {
    setSelectedPlanId(plan.infplId ?? null);
    eventBus.emit('selectPlan', plan.infplId ?? null);
    if (plan.bbox) {
      eventBus.emit('zoomInOnPlanBbox', plan.bbox);
    }
  };

  const getBeoordelingTooltip = (key: string) => {
    let tooltipText = contentService.getBeoordelingStatusDescription(key);
    return tooltipText;
  };

  const getBeoordelingIcon = (key: string) => {
    let icon = contentService.getBeoordelingStatusIcon(key);
    return icon;
  };

  const handleDetailPanelToggle = (planId: string | null) => {
    if (!planId) return;
    const id = String(planId);
    setExpandedDetailPanels(prev => ({
      ...prev,
      [id]: !prev[id]
    }));
  };

  const onClickPlanLink = useCallback(async (planId: string) => {
    onClickPlan(planId);
  }, [onClickPlan]);

  const gridColumns = useMemo(() => [
    {
      field: 'PlanId',
      headerName: 'Nummer',
      flex: 1,
      renderCell: (params: any) => <>{params.row.planId}</>,
    },
    {
      field: 'PlanNaam',
      headerName: 'Naam',
      flex: 3,
      renderCell: (params: any) => (
        <button
          type="button"
          className="table-link-button"
          onClick={() => onClickPlanLink(params.row.infplId)}
        >
          {params.row.planNaam}
        </button>
      ),
    },
    {
      field: 'Discipline',
      headerName: 'Discipline',
      flex: 1,
      renderCell: (params: any) => {
        const camelDiscipline = helperService.toCamelCase(params.row.discipline);
        return (
          <Tooltip title={params.row.discipline} placement="right" arrow>
            <span className={`category-icon category-icon-small ${camelDiscipline}-icon-white`}>
              {/* <span>{camelDiscipline}</span> */}
            </span>
          </Tooltip>
        );
      },
    },
    {
      field: 'OrganizationName',
      headerName: 'Organisatie',
      flex: 1,
      renderCell: (params: any) => <>{params.row.infplOrganizationName}</>,
    },
    {
      field: 'PlanType',
      headerName: 'Type',
      flex: 2,
      renderCell: (params: any) => <>{params.row.planType}</>,
    },
    {
      field: 'PlanStatus',
      headerName: 'Status',
      flex: 1,
      renderCell: (params: any) => <>{params.row.planStatus}</>,
    },
    {
      field: 'PlanStartDatum',
      headerName: 'Start',
      flex: 2,
      renderCell: (params: any) => {
        const value = params.row.planStartDatum;
        if (!value) return '';
        const date = new Date(value);
        return isNaN(date.getTime())
          ? ''
          : date.toLocaleDateString('nl-NL', { day: '2-digit', month: 'short', year: 'numeric' });
      }
    },
    {
      field: 'PlanEindDatum',
      headerName: 'Eind',
      flex: 2,
      renderCell: (params: any) => {
        const value = params.row.planEindDatum;
        if (!value) return '';
        const date = new Date(value);
        return isNaN(date.getTime())
          ? ''
          : date.toLocaleDateString('nl-NL', { day: '2-digit', month: 'short', year: 'numeric' });
      }
    },
    {
      field: 'Beoordeling',
      headerName: 'Beoordeling',
      sortable: false,
      flex: 1,
      renderCell: (params: any) => {

        // Geen waarde dan is dit een eigen plan
        if (!params.row.infplBeoordeling) {
          return (
            <></>
          );
        }
        
        const key = params.row.infplBeoordeling.id;
        let icon = getBeoordelingIcon(key);
        let tooltipText = getBeoordelingTooltip(key);

        return (
          <Tooltip title={tooltipText} placement="right" arrow>
            <span className={`beoordeling-icon ${key}`}>
              <span className="material-icons">{icon}</span>
            </span>
          </Tooltip>
        );
      },
    },
    {
      field: 'Kosten',
      headerName: 'Kosten',
      flex: 2,
      renderCell: (params: any) => {
        const value = params.row.kosten;
        return value != null
          ? new Intl.NumberFormat('nl-NL', {
              style: 'currency',
              currency: 'EUR',
            }).format(value)
          : '-';
      }
    },
    ...(authService.canCrud() ? [{
      field: 'Functies',
      headerName: 'Functies',
      renderHeader: () => <></>,
      width: 120,
      sortable: false,
      filterable: false,
      renderCell: (params: any) => {
        //     <PlannenActionMenu mijnOrganisatie={mijnOrganisatie} params={params} refetchPlannen={refetchPlannen} zoomInOnPlanBbox={handleZoomInOnPlanBbox} />
        const planId = params.row.infplId || '';
        const plan = params.row as InfraPlan;
        const isExpanded = expandedDetailPanels[planId] || false;
        return (
          <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
            <Tooltip title="Toon plan op kaart" arrow>
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  handleZoomInOnPlanBbox(plan);
                }}
              >
                <span className="material-icons">location_on</span>
              </IconButton>
            </Tooltip>
            <Tooltip title={isExpanded ? 'Sluiten' : 'Openen'} arrow>
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  handleDetailPanelToggle(planId);
                }}
                style={{
                  transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                  transition: 'transform 0.2s ease'
                }}
              >
                <span className="material-icons">expand_more</span>
              </IconButton>
            </Tooltip>
          </div>
        );
      },
    }] : []),
  ], [expandedDetailPanels, onClickPlanLink]);

  // Load stored column visibility from localStorage
  const [visibleColumns, setVisibleColumns] = useState<string[]>([]);

  const getDefaultColumns = useCallback((bp: string) => {
    if (bp === "xs") return ['PlanNaam'];
    if (bp === "sm") return ['PlanNaam', 'Functies'];
    if (bp === "md") return ['PlanId', 'PlanNaam', 'Functies'];
    if (bp === "lg") return ['PlanId', 'PlanNaam', 'OrganizationName', 'PlanStartDatum', 'Beoordeling', 'Functies'];
    if (bp === "xl") return ['PlanId', 'PlanNaam', 'OrganizationName', 'PlanStartDatum', 'PlanEindDatum', 'Beoordeling', 'Functies'];
    if (bp === "xxl") return ['PlanId', 'PlanNaam', 'OrganizationName', 'PlanStartDatum', 'PlanStatus', 'PlanType', 'Discipline', 'PlanStartDatum', 'PlanEindDatum', 'Beoordeling', 'Functies'];
    return [];
  }, []);
  
  const getVisibleColumns = useCallback(() => {
    const storedSettings = localStorage.getItem(LS_VISIBLE_COLUMN_SETTING_KEY);
    const parsedSettings = storedSettings ? JSON.parse(storedSettings) : {};

    if (parsedSettings[breakpoint]) {
      return parsedSettings[breakpoint];
    }
    return getDefaultColumns(breakpoint);
  }, [breakpoint, getDefaultColumns]);

  useEffect(() => {
    const columns = getVisibleColumns();
    setVisibleColumns(columns);
  }, [getVisibleColumns]);

  // Handle user toggling column visibility
  const handleColumnVisibilityChange = (newColumns: string[]) => {
    setVisibleColumns(newColumns);

    const storedSettings = localStorage.getItem(LS_VISIBLE_COLUMN_SETTING_KEY);
    const parsedSettings = storedSettings ? JSON.parse(storedSettings) : {};

    parsedSettings[breakpoint] = newColumns;
    localStorage.setItem(LS_VISIBLE_COLUMN_SETTING_KEY, JSON.stringify(parsedSettings));
  };

  const handleSortChange = (sortModel: GridSortModel) => {
    if (sortModel.length > 0) {
      const { field, sort } = sortModel[0]; // Get first sorting field
      const apiSortField = SORT_FIELD_MAP[field];
      if (apiSortField) {
        onSorting(apiSortField, sort || "asc"); // Pass sorting info to Sidebar
      }
      else {
        onSorting("", "");
      }
    }
    else {
      onSorting("", ""); // Reset sorting
    }
  };

  const onChangePagination = async ({ page, pageSize }: { page: number, pageSize: number }) => {
    setPaginationModel({ page, pageSize });
    onPagination(page, pageSize);
  };

  const refetchPlannen = async () => {
    // Use current pagination values
    onPagination(paginationModel.page, paginationModel.pageSize);
  };

  return (
    <>
      {/* {panelWidth} */}
      <div className="grid-responsive table-plannen">
        {/* <Paper sx={{ width: '100%' }}></Paper> */}
        <DataGrid
          localeText={{
            noRowsLabel: 'Geen plannen',
            MuiTablePagination: {
              labelRowsPerPage: 'Plannen per pagina:',
              labelDisplayedRows: ({ from, to, count }: { from: number; to: number; count: number }) => `${from}-${to} van ${count}`,
            },
          }}
          rows={plans.flatMap((plan) => {
            const planId = plan.infplId || '';
            const isExpanded = expandedDetailPanels[planId] || false;
            
            // Return array: [plan, ...optionalDetailRow]
            const result: any[] = [plan];
            if (isExpanded) {
              result.push({
                id: `${plan.infplId}_detail`,
                _isDetailRow: true,
                _plan: plan
              });
            }
            return result;
          })}
          columns={(() => {
            const firstVisibleField = gridColumns.find(c => visibleColumns.includes(c.field))?.field;
            return gridColumns.map((col) => ({
            ...col,
            sortable: col.sortable ?? Boolean(SORT_FIELD_MAP[col.field]),
            renderCell: (params: any) => {
              if (params.row._isDetailRow) {
                if (col.field === firstVisibleField) {
                  return (
                    <div style={{
                      padding: '16px',
                      backgroundColor: '#f9f9f9',
                      border: '1px solid #e0e0e0',
                      borderTop: 'none',
                      width: '100%',
                      boxSizing: 'border-box'
                    }}>
                      <div style={{
                        marginBottom: '8px',
                        fontWeight: 'bold',
                        color: '#333',
                      }}>
                        <label>Beoordelingen</label>
                      </div>
                      <BeoordelingenDetailPanel 
                        plan={params.row._plan}
                        mijnOrganisatie={mijnOrganisatie}
                        onEditBeoordeling={handleOpenBeoordelingDialog}
                        refreshKey={beoordelingRefreshKeys[params.row._plan.infplId] ?? 0}
                      />
                    </div>
                  );
                } else {
                  return null;
                }
              }
              // Normal row: render as usual
              return col.renderCell?.(params);
            }
          }));
          })()}
          disableColumnFilter // Disable filtering
          disableRowSelectionOnClick
          hideFooterSelectedRowCount
          getRowClassName={(params) => {
            if (params.row._isDetailRow) {
              return 'detail-row';
            }
            const isExpanded = expandedDetailPanels[params.row.infplId] || false;
            return `discipline-row ${helperService.toCamelCase(params.row.discipline)}-border${isExpanded ? ' row-expanded' : ''}`;
          }}
          rowCount={totalCount}
          sortModel={sortBy && sortDirection ? [{ field: sortBy, sort: sortDirection as 'asc' | 'desc' }] : []}
          paginationMode="server"
          pageSizeOptions={[5, 10, 25, 50, 100]}
          initialState={{
            pagination: {
              paginationModel: {
                page: pageIndex,
                pageSize: pageSize
              }
            }
          }}
          onPaginationModelChange={onChangePagination}
          sortingMode="server" // Disables built-in sorting
          onSortModelChange={handleSortChange} // Custom sort handling
          columnVisibilityModel={Object.fromEntries(gridColumns.map(col => [col.field, visibleColumns.includes(col.field)]))}
          onColumnVisibilityModelChange={(model) => handleColumnVisibilityChange(Object.keys(model).filter(key => model[key]))}
          onRowClick={(params) => {
            // Skip if it's a detail row
            if (params.row._isDetailRow) return;
          }}
          sx={{ 
            border: 0
          }}
          slotProps={{
            footer: { className: 'footer-sticky' }, // Add custom class // 
          }}
          rowSelectionModel={selectedPlanId && plans.some(p => p.infplId === selectedPlanId) ? [selectedPlanId] : []}
        />
      </div>

      {selectedPlanForBeoordeling && (
        <DialogBeoordelingAdd
          open={beoordelingDialogOpen}
          planNaam={selectedPlanForBeoordeling.planNaam || ''}
          onClose={handleCloseBeoordelingDialog}
          onSubmit={handleSubmitBeoordeling}
        />
      )}
    </>
  );
};

export default TablePlannen;